import type { Command } from 'commander';
import { randomUUID } from 'node:crypto';
import { personalDelete, type PersonalRequestOptions } from '../../../lib/personal-client';
import { resolveContext, type ResolvedContext } from '../../../lib/global-opts';
import { CliError, ExitCode } from '../../../lib/errors';
import { printJson, sanitizeForTerminal } from '../../../lib/output';
import { PERSONAL_RESUMES_BASE } from '@wport/core';

interface DeleteFlags {
	confirm?: boolean;
}

/** 同 view.ts 慣例：檔案小而聚焦，各檔各自複製一份，不額外拉一個共用模組。 */
function requireEncId(encId: string): string {
	const trimmed = encId.trim();
	if (!trimmed) {
		throw new CliError('enc_id must not be empty', ExitCode.InvalidArgument);
	}
	return trimmed;
}

/**
 * `resumes delete` 核心（R7，design doc §5.2）：破壞性、不可逆——無 `--confirm` 一律本地
 * exit 2、**不發請求**（同 jobs delete 慣例）。confirm 檢查先於 enc_id 驗證（同 jobs delete
 * 的 `runJobsDelete` 順序）。
 *
 * DELETE 成功後端回 `DataResponse(null)`（軟刪，`data` 為 null）——不 unwrap/deref（否則對 null
 * 取屬性會丟原生 TypeError，同 jobs/lifecycle.ts 的 F-001 教訓）；用呼叫時已驗證過的 enc_id
 * 回報刪除結果，不依賴回應 body。
 */
export async function runResumesDelete(ctx: ResolvedContext, encId: string, confirm: boolean): Promise<void> {
	if (!confirm) {
		throw new CliError('Refusing to delete without --confirm (destructive, irreversible)', ExitCode.InvalidArgument);
	}
	const trimmed = requireEncId(encId);
	const opts: PersonalRequestOptions = { baseUrl: ctx.baseUrl, locale: ctx.locale, timeoutMs: ctx.timeoutMs };

	await personalDelete(opts, `${PERSONAL_RESUMES_BASE}/${encodeURIComponent(trimmed)}`, { idempotencyKey: randomUUID() });

	if (ctx.format === 'json') {
		printJson({ enc_id: trimmed, deleted: true });
		return;
	}
	process.stdout.write(`Deleted resume: ${sanitizeForTerminal(trimmed)}\n`);
}

export function registerPersonalResumesDelete(parent: Command): void {
	parent
		.command('delete <enc_id>')
		.description('Delete a resume (destructive; requires --confirm)')
		.option('--confirm', 'confirm this destructive, irreversible delete')
		.action(async (encId: string, flags: DeleteFlags, command: Command) => {
			await runResumesDelete(resolveContext(command), encId, flags.confirm === true);
		});
}
